"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { useTranslation } from "react-i18next";
import { useProductUsage } from "./hooks/useProductUsage";
import { ChartProductUsageTotalValue } from "./components/chart-product-usage-total-value";
import { ChartProductUsageStackedBar } from "./components/chart-product-usage-stacked-bar";
import { ChartProductUsageLegend } from "./components/chart-product-usage-legend";
import { ChartProductUsageSkeleton } from "./components/chart-product-usage-skeleton";
import { formatCurrency } from "@/core/utils/formatters";
import { productTypeCharacteristics } from "@/core/utils/product-type-characteristics";

import { ChartProductUsageProps } from "./types";
import { useMemo } from "react";
import { DashboardEmptyState } from "../dashboard-empty-state";

export function DashboardChartProductUsage({
  startDate,
  endDate,
  city,
  group_id,
  type,
  status,
  users,
  spaces,
  viewMode,
}: ChartProductUsageProps) {
  const [translate] = useTranslation(["dashboard"]);

  const { data, isLoading, error, refetch } = useProductUsage({
    startDate,
    endDate,
    city,
    group_id,
    type,
    status,
    users,
    spaces,
  });

  const verifyEmptyData = useMemo(() => {
    if (isLoading || error) return false;

    if (!data || !data.products || data.products.length === 0) {
      return true;
    }

    // Verifica se todos os valores são zero
    const hasNoMonetaryValue = parseFloat(data.total.monetary_value) === 0;
    const hasNoCreditsValue = data.total.credits_value === 0;

    return hasNoMonetaryValue && hasNoCreditsValue;
  }, [data, isLoading, error]);

  const getColor = (type: number) =>
    productTypeCharacteristics[
      type as unknown as keyof typeof productTypeCharacteristics
    ]?.color ?? "primary-500";

  const getName = (type: number) =>
    translate(
      productTypeCharacteristics[
        type as unknown as keyof typeof productTypeCharacteristics
      ]?.name,
    ) ?? `${type}`;

  const totalLabel =
    viewMode === "currency"
      ? formatCurrency(data?.total.monetary_value ?? "0")
      : (data?.total.credits_value ?? 0);

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-none shadow-sm">
        <CardHeader className="pb-6 border-b border-secondary-500">
          <div className="flex flex-col gap-2 justify-start items-center lg:justify-between lg:flex-row">
            <CardTitle className="flex gap-2 items-center text-base font-normal text-darkgray-900">
              {translate("dashboard-chart-product-usage-title")}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="px-0 py-6">
          {isLoading && <ChartProductUsageSkeleton />}

          {(verifyEmptyData || error) && <DashboardEmptyState />}

          {!isLoading && !error && !verifyEmptyData && (
            <>
              <ChartProductUsageTotalValue valueLabel={totalLabel} />

              <ChartProductUsageStackedBar
                products={data?.products ?? []}
                viewMode={viewMode}
                totalMonetary={data?.total.monetary_value ?? "0"}
                totalCredits={data?.total.credits_value ?? 0}
                getColor={getColor}
              />

              <ChartProductUsageLegend
                products={data?.products ?? []}
                viewMode={viewMode}
                getColor={getColor}
                getName={getName}
                formatCurrency={formatCurrency}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
